import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjectPermits, setCurrentPermit, deletePermitForm } from '../../store/slices/projectPermitsSlice';
import Spinner from '../common/Spinner';
import PermitFormSelector from './PermitFormSelector';
import FormSelectionDialog from './FormSelectionDialog';
import { showToast } from '../utils/Toast';
import { openPermitPreviewPopup } from '../../utils/PermitPrintUtils';
import { config } from '../../config/config';

const PermitsTab = ({ projectId }) => {
  const dispatch = useDispatch();
  const { permits, formTypes, loading, error } = useSelector(state => state.projectPermits || {});
  const [selectedFormType, setSelectedFormType] = useState(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showFormSelectionDialog, setShowFormSelectionDialog] = useState(false);
  const [currentPermitId, setCurrentPermitId] = useState(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [permitToDelete, setPermitToDelete] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectPermits(projectId))
        .unwrap()
        .catch(error => {
          showToast(error || 'Failed to fetch permits', 'error');
        });
    }
  }, [projectId, dispatch]);

  const handleCreateForm = (formType) => {
    setSelectedFormType(formType);
    setCurrentPermitId(null);
    dispatch(setCurrentPermit(null));
    setShowFormDialog(true);
  };

  const handleEditForm = (permit) => {
    setSelectedFormType(permit.formType);
    setCurrentPermitId(permit._id);
    dispatch(setCurrentPermit(permit));
    setShowFormDialog(true);
  };

  const handleCloseForm = () => {
    setShowFormDialog(false);
    setSelectedFormType(null);
    setCurrentPermitId(null);
    dispatch(setCurrentPermit(null));
    
    // Refresh the permits list
    dispatch(fetchProjectPermits(projectId));
  };

  const handleDeleteForm = (permit) => {
    setPermitToDelete(permit);
    setShowDeleteConfirmDialog(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailRecipient.trim()) {
      showToast('Please enter a recipient email', 'error');
      return;
    }
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiUrl}/api/projects/${projectId}/permits/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientEmail: emailRecipient.trim(), note: emailNote.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send email');
      showToast(data.message || 'Permits sent successfully!', 'success');
      setShowEmailModal(false);
      setEmailRecipient('');
      setEmailNote('');
    } catch (err) {
      showToast(err.message || 'Failed to send permits email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleViewPrint = (permit) => {    const opened = openPermitPreviewPopup({
      permit,
      formTitle: getFormTypeLabel(permit.formType)
    });

    if (!opened) {
      showToast('Popup was blocked. Please allow popups and try again.', 'error');
    }
  };

  const confirmDeleteForm = async () => {
    try {
      await dispatch(deletePermitForm({
        projectId,
        permitId: permitToDelete._id
      })).unwrap();
      
      showToast('Form deleted successfully', 'success');
      setShowDeleteConfirmDialog(false);
      setPermitToDelete(null);
      
      // Refresh the permits list
      dispatch(fetchProjectPermits(projectId));
    } catch (error) {
      showToast(error || 'Failed to delete form', 'error');
    }
  };

  const getFormTypeLabel = (formType) => {
    const form = formTypes.find(f => f.id === formType);
    return form ? form.name : formType;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !permits.length) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" color="blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => dispatch(fetchProjectPermits(projectId))}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  const currentPermit = permits.find(p => p._id === currentPermitId);

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmDialog && permitToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Form
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete the "{getFormTypeLabel(permitToDelete.formType)}" form? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  setShowDeleteConfirmDialog(false);
                  setPermitToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={confirmDeleteForm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send by Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-700 to-blue-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-lg font-semibold text-white">Send All Permits by Email</h3>
              </div>
              <button onClick={() => { setShowEmailModal(false); setEmailRecipient(''); setEmailNote(''); }} className="text-white/70 hover:text-white">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSendEmail} className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
                All <strong>{permits.length}</strong> permit form{permits.length !== 1 ? 's' : ''} for this project will be sent as a formatted email with Topsail Roofing branding.
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Recipient Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  required
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="e.g. client@example.com"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Note <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={emailNote}
                  onChange={(e) => setEmailNote(e.target.value)}
                  placeholder="Add a personal message or instructions..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowEmailModal(false); setEmailRecipient(''); setEmailNote(''); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingEmail}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showFormDialog ? (
        <PermitFormSelector
          formType={selectedFormType}
          projectId={projectId}
          currentPermit={currentPermit}
          onClose={handleCloseForm}
        />
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Project Permits & Forms</h2>
            <div className="flex items-center gap-2">
              {permits.length > 0 && (
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 text-sm"
                  onClick={() => setShowEmailModal(true)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send All by Email
                </button>
              )}
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                onClick={() => setShowFormSelectionDialog(true)}
              >
                <span>Add New Form</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Form Selection Dialog */}
          <FormSelectionDialog
            isOpen={showFormSelectionDialog}
            onClose={() => setShowFormSelectionDialog(false)}
            formTypes={formTypes}
            onSelectForm={handleCreateForm}
          />

          {permits.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Form Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Updated
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {permits.map((permit) => (
                    <tr key={permit._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getFormTypeLabel(permit.formType)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {formatDate(permit.updatedAt || permit.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {permit.createdBy?.name || 'System'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleEditForm(permit)}
                          className="text-indigo-600 hover:text-indigo-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleViewPrint(permit)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          View/Print
                        </button>
                        <button
                          onClick={() => handleDeleteForm(permit)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No forms yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new form for this project.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowFormSelectionDialog(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create New Form
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PermitsTab;
